import { type KeyboardEvent, useCallback, useRef } from 'react';

export type ArrowNavOrientation = 'horizontal' | 'vertical';

/**
 * Where an arrow key moves focus within a group of controls, or null when the
 * key is not one this group handles.
 *
 * A toolbar wraps and a list clamps, which is the difference between "keep
 * pressing right to cycle the filters" and "hold down at the bottom of a
 * thousand threads without being thrown back to the top".
 */
export function nextArrowStopIndex({
    key,
    orientation,
    currentIndex,
    count,
}: {
    readonly key: string;
    readonly orientation: ArrowNavOrientation;
    readonly currentIndex: number;
    readonly count: number;
}): number | null {
    if (count === 0) {
        return null;
    }
    const forwardKey =
        orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    const backwardKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';

    if (key === 'Home') {
        return 0;
    }
    if (key === 'End') {
        return count - 1;
    }
    if (key !== forwardKey && key !== backwardKey) {
        return null;
    }

    const step = key === forwardKey ? 1 : -1;
    // Focus is somewhere else in the group (or nowhere): enter at whichever
    // end the key is travelling from.
    if (currentIndex < 0) {
        return step === 1 ? 0 : count - 1;
    }
    const next = currentIndex + step;
    if (orientation === 'horizontal') {
        return (next + count) % count;
    }
    return Math.min(Math.max(next, 0), count - 1);
}

/**
 * Arrow-key movement across a group of controls that are already tabbable.
 * Tab, Shift+Tab, Space and Enter keep their native behaviour -- this only
 * adds the shorter path between neighbours.
 */
export function useInboxArrowNavigation<Element extends HTMLElement>({
    orientation,
    stopSelector,
}: {
    readonly orientation: ArrowNavOrientation;
    readonly stopSelector: string;
}) {
    const containerRef = useRef<Element>(null);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<Element>) => {
            const container = containerRef.current;
            if (!container || event.defaultPrevented) {
                return;
            }
            // A text field owns its own caret keys, and an open menu owns
            // arrows for its items.
            const target = event.target as HTMLElement | null;
            if (
                target?.tagName === 'INPUT' ||
                target?.tagName === 'TEXTAREA' ||
                target?.isContentEditable
            ) {
                return;
            }

            const stops = Array.from(
                container.querySelectorAll<HTMLElement>(stopSelector),
            );
            const nextIndex = nextArrowStopIndex({
                key: event.key,
                orientation,
                currentIndex: target ? stops.indexOf(target) : -1,
                count: stops.length,
            });
            if (nextIndex === null) {
                return;
            }
            // Only claim the key once there is somewhere to go, so an
            // unhandled arrow still scrolls the pane.
            event.preventDefault();
            stops[nextIndex]?.focus();
        },
        [orientation, stopSelector],
    );

    return { containerRef, handleKeyDown };
}
